import React, { useState, useEffect, useContext } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import "./Products.css";
import { ConfigContext } from "../Context/ConfigContext";

function Products() {
  const { slug, search_query } = useParams();
  const params_category_id = (slug && slug != 0) ? slug.split("-").pop() : null;
  const { ApiUrl, apiHeaderJson } = useContext(ConfigContext);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(params_category_id ? Number(params_category_id) : "all");
  const [searchTerm, setSearchTerm] = useState(search_query || "");

  // Fetch products and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch products
        const productResponse = await axios.get(`${ApiUrl}apis/GetProducts`, {
          headers: apiHeaderJson,
        });
        // Fetch categories
        const categoryResponse = await axios.get(`${ApiUrl}apis/CategoryList`, {
          headers: apiHeaderJson,
        });

        let productData = [];
        let categoryData = [];

        if (productResponse.data.success && Array.isArray(productResponse.data.data)) {
          productData = productResponse.data.data;
        }

        if (categoryResponse.data.success && Array.isArray(categoryResponse.data.data)) {
          categoryData = categoryResponse.data.data.map((cat) => ({
            id: cat.category_id || cat.id,
            name: cat.category_title || cat.name,
          }));
        }

        setProducts(productData);
        setCategories(categoryData);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError(err.message || "Failed to load products");
        setProducts([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ApiUrl, apiHeaderJson]);

  // Helper: get category name by ID
  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat ? cat.name : null;
  };

  // Filter products
  const filteredProducts = products.filter((product) => {
    const matchesCategory = activeCategory === "all" || product.category_id === activeCategory;

    const matchesSearch = product.product_title.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  // Build product display object with necessary fields
  const displayProducts = filteredProducts.map((product) => ({
    id: product.product_id,
    name: product.product_title,
    category: getCategoryName(product.category_id) || '',
    category_id: product.category_id,
    image: product.product_image_url || "",
    slug: `${product.product_title.toLowerCase().replace(/\s+/g, "-")}-${product.product_id}`,
    badge: null,
  }));

  // Loading state
  if (loading) {
    return (
      <div className="products-page">
        <section className="products-hero">
          <div className="products-hero-content">
            <h1>Loading Products...</h1>
          </div>
        </section>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="products-page">
        <section className="products-hero">
          <div className="products-hero-content">
            <h1>Error</h1>
            <p>{error}</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="products-page">
      {/* Page Header */}
      <section className="products-hero">
        <div className="products-hero-content" data-aos="fade-up">
          <span className="products-hero-tag">OUR COLLECTION</span>
          <h1>Explore Our <span>Products</span></h1>
          <p>Discover the delicious range of biscuits, cakes and breads crafted with love.</p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="products-filter-bar">
        <div className="products-filter-container" data-aos="fade-up">
          <div className="category-tabs">
            <button
              className={`category-tab ${activeCategory === "all" ? "active" : ""}`}
              onClick={() => setActiveCategory("all")}
            >
              All Categories
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`category-tab ${activeCategory === cat.id ? "active" : ""}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="products-search">
            <i className="ri-search-line"></i>
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="products-grid-section">
        <div className="products-grid-container">
          {displayProducts.length > 0 ? (
            <div className="products-grid">
              {displayProducts.map((product, index) => (
                <Link
                  to={`/product/${product.slug}`}
                  key={product.id}
                  className="product-card"
                  data-aos="fade-up"
                  data-aos-delay={(index % 4) * 100}
                >
                  <div className="product-card-image">
                    {product.badge && (
                      <span className="product-card-badge">{product.badge}</span>
                    )}
                    <img src={product.image} alt={product.name} loading="lazy" />
                  </div>
                  <div className="product-card-body">
                    {product.category && <span className="product-card-category">{product.category}</span>}
                    <h3>{product.name}</h3>
                    <div className="product-card-footer">
                      <span className="product-card-link">
                        View Details <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="products-empty">
              <i className="ri-search-eye-line"></i>
              <h3>No products found</h3>
              <p>Try adjusting your search or category filter.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Products;